import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { CalendarCheck, Clock, ShieldCheck, Sparkles, Phone, Mail, CheckCircle2 } from "lucide-react";
import { useBranding } from "../lib/branding";
import AdvisorCard from "../components/AdvisorCard";

/**
 * Booking page = Calendly iframe with the visitor's intake-form data pre-filled.
 *
 * Calendly URL params we use:
 *   name              – prefilled into the "Name" field
 *   email             – prefilled into the "Email" field
 *   a1                – prefilled into the first custom question (configure Calendly:
 *                       Question 1 = "Phone number" so the lead doesn't re-type it)
 *   utm_content       – stashes the lead_id so we can correlate webhook bookings back to
 *                       the original submission later.
 *
 * The Calendly event should be configured with ONLY these short fields:
 *   - Name (auto-prefilled)
 *   - Email (auto-prefilled)
 *   - Question 1 · "Phone number" (auto-prefilled)
 *   - Question 2 · "Anything else you'd like us to know? (optional)" — free-text notes
 *
 * All annuity-qualification questions stay on the in-app intake form.
 */
export default function Booking() {
    const { branding } = useBranding();
    const location = useLocation();
    const navigate = useNavigate();
    const lead = location.state || {};
    const calendlyLink = branding?.owner_calendly_link;
    const [booked, setBooked] = useState(false);

    const embedSrc = buildCalendlyEmbedSrc(calendlyLink, lead);

    // Listen for the Calendly inline embed postMessage event 'calendly.event_scheduled'.
    // Works on the free Calendly tier — no webhook needed. When the visitor confirms
    // their booking, we fast-forward them to the "Prepare For Your Review" upsell page.
    useEffect(() => {
        function handleCalendlyEvent(e) {
            if (!e?.data?.event || typeof e.data.event !== "string") return;
            if (!e.data.event.startsWith("calendly.")) return;
            if (e.data.event === "calendly.event_scheduled") {
                setBooked(true);
                // Brief celebration moment before navigating
                setTimeout(() => {
                    navigate("/prepare-review", { state: lead, replace: false });
                }, 1400);
            }
        }
        window.addEventListener("message", handleCalendlyEvent);
        return () => window.removeEventListener("message", handleCalendlyEvent);
    }, [navigate, lead]);

    return (
        <div className="relative overflow-hidden bg-navy-900 min-h-screen py-16 md:py-24" data-testid="booking-page">
            {/* Decorative background — matches the brand */}
            <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-br from-navy-900 via-[#0B2F3A] to-emerald-900/70" />
                <div className="absolute -top-32 -right-40 w-[560px] h-[560px] rounded-full bg-amber-400/10 blur-3xl" />
                <div className="absolute -bottom-32 -left-32 w-[480px] h-[480px] rounded-full bg-emerald-400/10 blur-3xl" />
            </div>

            <div className="relative max-w-5xl mx-auto px-6 sm:px-8">
                <div className="text-center max-w-3xl mx-auto">
                    <div className="inline-flex items-center gap-2 uppercase tracking-[0.25em] text-[11px] font-bold text-amber-300 mb-5 bg-amber-400/10 border border-amber-400/30 rounded-full px-4 py-1.5">
                        <Sparkles className="w-3 h-3" /> Schedule Your Review
                    </div>
                    <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white leading-tight">
                        You're one step from <span className="text-amber-300">clarity.</span>
                    </h1>
                    <p className="mt-6 text-lg text-blue-50/85 leading-relaxed">
                        Book a quick 15-minute call so we can walk through your annuity together. No sales pitch — just clear answers.
                    </p>
                </div>

                {/* Trust pills */}
                <div className="mt-10 grid sm:grid-cols-3 gap-3 max-w-2xl mx-auto">
                    <Pill icon={Clock} text="15-minute call" tone="emerald" />
                    <Pill icon={ShieldCheck} text="No pressure" tone="amber" />
                    <Pill icon={CalendarCheck} text="Pick your time" tone="sky" />
                </div>

                {/* Reassurance — only when the visitor came from the intake form */}
                {lead.leadId && (
                    <div className="mt-8 max-w-2xl mx-auto bg-emerald-400/10 border border-emerald-400/30 rounded-xl px-5 py-3 text-center text-emerald-100" data-testid="booking-prefill-banner">
                        <p className="text-sm">
                            <span className="font-semibold">{lead.name?.split(" ")[0] || "You"},</span> we've already received your annuity details from the intake form — no need to repeat anything. Just pick a time below.
                        </p>
                    </div>
                )}

                {/* Calendly + Advisor sidebar */}
                <div className={`mt-14 ${lead.advisor && lead.advisor.name ? "grid lg:grid-cols-12 gap-6 items-start" : ""}`}>
                    {lead.advisor && lead.advisor.name && (
                        <aside className="lg:col-span-4 order-2 lg:order-1" data-testid="booking-advisor-aside">
                            <AdvisorCard advisor={lead.advisor} compact tone="dark" />
                            <p className="mt-4 text-center text-xs text-blue-100/70">
                                Your assigned advisor will personally lead the call.
                            </p>
                        </aside>
                    )}
                    <div className={`${lead.advisor && lead.advisor.name ? "lg:col-span-8 order-1 lg:order-2" : ""} bg-white border border-slate-200 rounded-2xl p-3 sm:p-5 shadow-2xl relative overflow-hidden`}>
                        {/* Top accent strip */}
                        <span className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-amber-400 via-rose-400 to-emerald-400" />

                        <div className="rounded-xl overflow-hidden bg-white border border-slate-200" style={{ minHeight: "720px" }}>
                            {embedSrc ? (
                                <iframe
                                    data-testid="calendly-embed"
                                    title="Schedule your annuity review"
                                    src={embedSrc}
                                    style={{ width: "100%", minHeight: "720px", border: 0 }}
                                />
                            ) : (
                                <div className="flex items-center justify-center min-h-[720px] bg-slate-50 text-slate-600 text-center p-8">
                                    <div>
                                        <p className="text-lg font-semibold">Booking link not yet configured.</p>
                                        <p className="mt-2 text-sm">Please email <a className="text-amber-600 font-semibold" href="mailto:reviews@yourannuityreview.com">reviews@yourannuityreview.com</a> and we'll get you scheduled directly.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Fallback contact */}
                <div className="mt-8 max-w-2xl mx-auto bg-white/5 backdrop-blur border border-white/10 rounded-xl p-5 text-center text-blue-100/85">
                    <p className="text-sm">Calendar not loading?</p>
                    <div className="mt-2 flex flex-wrap justify-center gap-x-6 gap-y-2 text-sm">
                        <a href="mailto:reviews@yourannuityreview.com" className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 font-semibold">
                            <Mail className="w-4 h-4" /> reviews@yourannuityreview.com
                        </a>
                        <a href="tel:+19185550100" className="inline-flex items-center gap-2 text-amber-300 hover:text-amber-200 font-semibold">
                            <Phone className="w-4 h-4" /> Call us directly
                        </a>
                    </div>
                </div>

                {/* Manual fallback if the postMessage doesn't fire (ad blockers, network) */}
                {lead.leadId && (
                    <div className="mt-6 max-w-2xl mx-auto text-center">
                        <button
                            onClick={() => navigate("/prepare-review", { state: lead })}
                            data-testid="manual-booked-continue"
                            className="text-amber-300/80 hover:text-amber-200 text-sm underline underline-offset-4"
                        >
                            Already booked? Continue to "Prepare For Your Review" →
                        </button>
                    </div>
                )}
            </div>

            {/* Booking-confirmed celebration overlay (1.4s before redirect) */}
            {booked && (
                <div className="fixed inset-0 z-50 bg-navy-900/95 backdrop-blur-sm flex items-center justify-center" data-testid="booking-confirmed-overlay">
                    <div className="text-center px-6">
                        <div className="mx-auto w-20 h-20 rounded-full bg-emerald-500/20 border-4 border-emerald-400 flex items-center justify-center animate-pulse">
                            <CheckCircle2 className="w-10 h-10 text-emerald-300" />
                        </div>
                        <h2 className="mt-6 font-serif text-3xl text-white">Appointment confirmed</h2>
                        <p className="mt-3 text-blue-100/80 text-sm">Loading your "Prepare For Your Review" page…</p>
                    </div>
                </div>
            )}
        </div>
    );
}

const TONES = {
    emerald: "bg-emerald-400/15 border-emerald-400/30 text-emerald-200",
    amber: "bg-amber-400/15 border-amber-400/30 text-amber-200",
    sky: "bg-sky-400/15 border-sky-400/30 text-sky-200",
};

function Pill({ icon: Icon, text, tone = "emerald" }) {
    const t = TONES[tone] || TONES.emerald;
    return (
        <div className={`flex items-center justify-center gap-2 rounded-full px-4 py-3 border backdrop-blur ${t}`}>
            <Icon className="w-4 h-4" />
            <span className="text-sm font-semibold">{text}</span>
        </div>
    );
}

function buildCalendlyEmbedSrc(calendlyLink, lead) {
    if (!calendlyLink) return null;
    const params = new URLSearchParams({
        hide_landing_page_details: "1",
        hide_gdpr_banner: "1",
    });
    if (lead.name) params.set("name", lead.name);
    if (lead.email) params.set("email", lead.email);
    // Calendly maps custom questions in order: a1, a2, a3...
    // We pre-fill question 1 with the phone number — the host should configure
    // the event's first custom question as "Phone number".
    if (lead.phone) params.set("a1", lead.phone);
    // Stash lead_id so the Calendly webhook can correlate the booking later
    if (lead.leadId) params.set("utm_content", lead.leadId);
    const join = calendlyLink.includes("?") ? "&" : "?";
    return `${calendlyLink}${join}${params.toString()}`;
}
